import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import {
    getTournamentById, 
    registerForTournament, 
    unregisterFromTournament,
    getTournamentBracket
} from '../api/tournaments';
import { subscribeToTournament } from '../api/websocket';
import HrnbButton from "../components/controls/HrnbButton";
import HrnbAlert from "../components/controls/HrnbAlert";
import HrnbLoader from "../components/controls/HrnbLoader";
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import TournamentBracket from '../components/duels/TournamentBracket';
import '../css/pages/TournamentDetailPage.css';
import HrnbModal from "../components/controls/HrnbModal";

/**
 * Страница деталей турнира
 */
function TournamentDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    const [tournament, setTournament] = useState(null);
    const [bracket, setBracket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showUnregisterModal, setShowUnregisterModal] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [wsSubscription, setWsSubscription] = useState(null);

    // Загрузка деталей турнира
    useEffect(() => {
        const loadTournament = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await getTournamentById(id);
                setTournament(data);
            } catch (err) {
                console.error('Ошибка при загрузке турнира:', err);
                setError('Не удалось загрузить турнир. Возможно, он был удалён.');
            } finally {
                setLoading(false);
            }
        };

        loadTournament();
    }, [id]);

    // Загрузка сетки турнира
    useEffect(() => {
        if (id && activeTab === 'bracket') {
            loadBracket();
        }
    }, [id, activeTab]);

    const loadBracket = async () => {
        try {
            const data = await getTournamentBracket(id);
            setBracket(data);
        } catch (err) {
            console.error('Ошибка при загрузке сетки:', err);
        }
    };

    // WebSocket подписка
    useEffect(() => {
        if (!tournament) return;

        const subscription = subscribeToTournament(id, (event) => {
            console.log('[Tournament] WebSocket event:', event);
            // Обновляем данные турнира при событиях
            getTournamentById(id).then(setTournament).catch(console.error);
        });

        setWsSubscription(subscription);

        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
        };
    }, [id, tournament]);

    // Проверка участия
    const isParticipant = tournament?.participants?.some(
        p => p.profileId === currentUser?.id
    );
    const isOrganizer = tournament?.organizer?.profileId === currentUser?.id;
    const canRegister = tournament?.status === 'REGISTRATION' && !isParticipant;
    const canUnregister = isParticipant && tournament?.status === 'REGISTRATION';

    // Обработчик регистрации
    const handleRegister = async () => {
        setActionLoading(true);
        try {
            await registerForTournament(id, {});
            const updated = await getTournamentById(id);
            setTournament(updated);
        } catch (err) {
            console.error('Ошибка при регистрации:', err);
            setError(err.response?.data?.message || 'Не удалось зарегистрироваться');
        } finally {
            setActionLoading(false);
        }
    };

    // Обработчик отмены регистрации
    const handleUnregister = async () => {
        setActionLoading(true);
        try {
            await unregisterFromTournament(id);
            const updated = await getTournamentById(id);
            setTournament(updated);
            setShowUnregisterModal(false);
        } catch (err) {
            console.error('Ошибка при отмене регистрации:', err);
            setError(err.response?.data?.message || 'Не удалось отменить регистрацию');
        } finally {
            setActionLoading(false);
        }
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Статусы
    const statusLabels = {
        REGISTRATION: 'Регистрация',
        UPCOMING: 'Скоро начнётся',
        ACTIVE: 'Активен',
        FINISHED: 'Завершён',
        CANCELLED: 'Отменён'
    };

    const statusColors = {
        REGISTRATION: '#22c55e',
        UPCOMING: '#3b82f6',
        ACTIVE: '#8b5cf6',
        FINISHED: '#6b7280',
        CANCELLED: '#ef4444'
    };

    if (loading) {
        return <HrnbLoader />;
    }

    if (error && !tournament) {
        return (
            <div className="tournament-detail">
                <HrnbAlert variant="danger">{error}</HrnbAlert>
                <Link to="/tournaments">
                    <HrnbButton variant="secondary">Вернуться к списку</HrnbButton>
                </Link>
            </div>
        );
    }

    return (
        <div className="tournament-detail">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbTabs value={activeTab} onChange={setActiveTab}>
                <HrnbTab label="Информация" value="info" />
                <HrnbTab label="Участники" value="participants" />
                <HrnbTab label="Сетка" value="bracket" />
            </HrnbTabs>

            <div className="tournament-detail__content">
                {activeTab === 'info' && (
                    <>
                        <div className="tournament-detail__header">
                            <div className="tournament-detail__badges">
                                <span 
                                    className="tournament-detail__status"
                                    style={{ backgroundColor: statusColors[tournament.status] }}
                                >
                                    {statusLabels[tournament.status] || tournament.status}
                                </span>
                                <span className="tournament-detail__format">
                                    {tournament.format === 'SINGLE_ELIMINATION' ? 'Одиночное выбывание' : 
                                     tournament.format === 'DOUBLE_ELIMINATION' ? 'Двойное выбывание' :
                                     tournament.format === 'ROUND_ROBIN' ? 'Круговая система' : 'Швейцарская'}
                                </span>
                            </div>

                            <h1 className="tournament-detail__title">{tournament.title}</h1>
                        </div>

                        {tournament.description && (
                            <p className="tournament-detail__description">{tournament.description}</p>
                        )}

                        <div className="tournament-detail__meta">
                            <div className="tournament-detail__meta-item">
                                <span className="tournament-detail__meta-label">Призовой фонд:</span>
                                <span className="tournament-detail__meta-value">
                                    {tournament.prizePoolAmount 
                                        ? `${tournament.prizePoolAmount.toLocaleString('ru-RU')} ${tournament.currency || 'RUB'}`
                                        : tournament.prizePool || '—'
                                    }
                                </span>
                            </div>

                            <div className="tournament-detail__meta-item">
                                <span className="tournament-detail__meta-label">Участников:</span>
                                <span className="tournament-detail__meta-value">
                                    {tournament.currentParticipants} / {tournament.maxParticipants}
                                </span>
                            </div>

                            <div className="tournament-detail__meta-item">
                                <span className="tournament-detail__meta-label">Взнос:</span>
                                <span className="tournament-detail__meta-value">
                                    {tournament.entryFee ? `${tournament.entryFee} RUB` : 'Бесплатно'}
                                </span>
                            </div>

                            <div className="tournament-detail__meta-item">
                                <span className="tournament-detail__meta-label">Начало:</span>
                                <span className="tournament-detail__meta-value">
                                    {formatDate(tournament.startDate)}
                                </span>
                            </div>

                            {tournament.registrationDeadline && (
                                <div className="tournament-detail__meta-item">
                                    <span className="tournament-detail__meta-label">Регистрация до:</span>
                                    <span className="tournament-detail__meta-value">
                                        {formatDate(tournament.registrationDeadline)}
                                    </span>
                                </div>
                            )}

                            <div className="tournament-detail__meta-item">
                                <span className="tournament-detail__meta-label">Организатор:</span>
                                <Link
                                    to={`/profile/${tournament.organizer?.profileId}`}
                                    className="tournament-detail__meta-value tournament-detail__link"
                                >
                                    {tournament.organizer?.displayName}
                                </Link>
                            </div>
                        </div>

                        {/* Действия */}
                        <div className="tournament-detail__actions">
                            {canRegister && (
                                <HrnbButton
                                    variant="success"
                                    onClick={handleRegister}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                                </HrnbButton>
                            )}

                            {canUnregister && (
                                <HrnbButton
                                    variant="danger"
                                    onClick={() => setShowUnregisterModal(true)}
                                    disabled={actionLoading}
                                >
                                    Отменить регистрацию
                                </HrnbButton>
                            )}

                            {isParticipant && tournament.status === 'ACTIVE' && (
                                <Link to={`/tournaments/${id}/match`}>
                                    <HrnbButton variant="primary">
                                        Мой матч
                                    </HrnbButton>
                                </Link>
                            )}

                            <Link to="/tournaments">
                                <HrnbButton variant="secondary">
                                    Назад к списку
                                </HrnbButton>
                            </Link>
                        </div>
                    </>
                )}

                {activeTab === 'participants' && tournament.participants && (
                    <div className="tournament-detail__participants">
                        <h2 className="tournament-detail__section-title">
                            Участники ({tournament.participants.length})
                        </h2>
                        <div className="tournament-detail__participants-list">
                            {tournament.participants.map((participant, index) => (
                                <div key={participant.id || participant.profileId} className="tournament-detail__participant">
                                    <span className="tournament-detail__participant-seed">
                                        #{participant.seed || index + 1}
                                    </span>
                                    <Link
                                        to={`/profile/${participant.profileId}`}
                                        className="tournament-detail__participant-name"
                                    >
                                        {participant.displayName}
                                    </Link>
                                    {participant.teamName && (
                                        <span className="tournament-detail__participant-team">
                                            {participant.teamName}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'bracket' && (
                    <div className="tournament-detail__bracket">
                        {bracket ? (
                            <TournamentBracket bracket={bracket} tournamentId={id} />
                        ) : (
                            <div className="tournament-detail__bracket-empty">
                                <p>Сетка турнира будет доступна после начала</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Модальное окно отмены регистрации */}
            <HrnbModal
                open={showUnregisterModal}
                onClose={() => setShowUnregisterModal(false)}
                title="Отмена регистрации"
            >
                <p>Вы уверены, что хотите отменить регистрацию на турнир?</p>
                <div className="tournament-detail__modal-actions">
                    <HrnbButton
                        variant="danger"
                        onClick={handleUnregister}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Отмена...' : 'Да, отменить'}
                    </HrnbButton>
                    <HrnbButton
                        variant="secondary"
                        onClick={() => setShowUnregisterModal(false)}
                        disabled={actionLoading}
                    >
                        Нет
                    </HrnbButton>
                </div>
            </HrnbModal>
        </div>
    );
}

export default TournamentDetailPage;
